import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Collapse,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  InputAdornment,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import BoltIcon from '@mui/icons-material/Bolt';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import HdIcon from '@mui/icons-material/Hd';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import MemoryIcon from '@mui/icons-material/Memory';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import StorefrontIcon from '@mui/icons-material/Storefront';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';

import { useAuth } from '../../../hooks';
import { useRuntimeActions, useRuntimeChannel, useRuntimeOutput } from '../../../contexts/ConsoleContext';
import { useSnackbar } from '../../../contexts/SnackbarContext';
import { AddonStore } from '../../../components/portal/AddonStore';
import { DestinationModal } from '../../../components/modals/DestinationModal';
import { HLSModal } from '../../../components/modals/HLSModal';
import { PLATFORM_PRESETS } from '../../../constants/platformPresets';
import {
  createTranscoder,
  updateTranscoder,
  rotateTranscoderPullKey,
  rotateTranscoderPullSecret,
  clearTranscoderPullSecret,
  deleteTranscoder,
  disconnectPullOutput,
  disconnectHlsOutput,
  DeliveryMode,
  PullProtocol,
  Transcoder,
  UpdateTranscoderPayload,
  PullOutputStatusEnvelope,
} from '../../../services/streamsService';
import {
  CapacityBar,
  FilterKey,
  STATUS_COLOR,
  StatCell,
  buildGeoLabel,
  buildHlsGeoLabel,
  buildRistPreviewPullUrl,
  buildSrtPreviewPullUrl,
  consoleFilterChipSx,
  consoleIconButtonSx,
  copyText,
  consoleSectionHeadingSx,
  fieldSx,
  formatBytes,
  formatDateTime,
  formatKbps,
  getDestinationHealthMetrics,
  getPullHlsDisplayState,
  isManagedServiceOutput,
  isPullEligible,
  isPullOutput,
  isSocialStreamOutput,
  labelSx,
  monoSx,
  pulseDot,
  smallBtnSx,
} from './DestinationsShared';
import { ConsolePageShell } from '../../../components/console/ConsolePageShell';
import { ConsoleDialogFrame } from '../../../components/console/ConsoleDialogFrame';
import { themedAlpha } from '../../../utils/themeTokens';
type HlsMode = 'HLS_STANDARD' | 'HLS_SCTE35';

const actionBtnSx = (color: string, prominent = false) => ({
  borderRadius: 0,
  color: prominent ? 'var(--on-primary)' : color,
  borderColor: color,
  bgcolor: prominent ? color : themedAlpha(color, 0.08),
  px: 1.25,
  minHeight: 34,
  fontSize: '0.72rem',
  fontWeight: 800,
  textTransform: 'none' as const,
  '&:hover': { bgcolor: prominent ? themedAlpha(color, 0.84) : themedAlpha(color, 0.18), borderColor: color },
});

const normalizePullProtocol = (value?: PullProtocol | string | null): PullProtocol =>
  String(value || 'SRT').toUpperCase() === 'RIST' ? 'RIST' : 'SRT';

const getRistBaseUrl = (t: Transcoder, status?: PullOutputStatusEnvelope['status']) =>
  (status as any)?.ristPullUrl ||
  (status as any)?.pullRistUrl ||
  (t as any).ristPullUrl ||
  (t as any).pullRistUrl ||
  status?.pullUrl ||
  t.destination ||
  '';

const getSrtBaseUrl = (t: Transcoder, status?: PullOutputStatusEnvelope['status']) =>
  (status as any)?.srtPullUrl ||
  (status as any)?.pullSrtUrl ||
  (t as any).srtPullUrl ||
  (t as any).pullSrtUrl ||
  status?.pullUrl ||
  t.destination ||
  '';

const DestinationRow = React.memo(({
  render,
}: {
  transcoder: Transcoder;
  signature: string;
  themeMode: string;
  render: () => React.ReactElement;
}) => render(), (previous, next) => (
  previous.transcoder === next.transcoder &&
  previous.signature === next.signature &&
  previous.themeMode === next.themeMode
));

export const DestinationsView: React.FC = () => {
  const { user } = useAuth();
  const { activeChannel } = useRuntimeChannel();
  const { refreshData } = useRuntimeActions();
  const {
    transcoders = [],
    pullStatuses: runtimePullStatuses,
    hlsStatuses: runtimeHlsStatuses,
    outputHealthStatuses,
  } = useRuntimeOutput();
  const { showSnackbar } = useSnackbar();
  const theme = useTheme();

  const [isModalOpen, setModalOpen] = useState(false);
  const [editData, setEditData] = useState<Transcoder | undefined>();
  const [addType, setAddType] = useState<'DIRECT' | 'MANAGED' | 'SERVICE' | 'SOCIAL' | null>(null);
  const [hlsMode, setHlsMode] = useState<HlsMode | null>(null);
  const [isStoreOpen, setStoreOpen] = useState(false);
  const [filter, setFilter] = useState<FilterKey>('ALL');
  const [expanded, setExpanded] = useState<Record<number, boolean>>({});

  const [pullLabelDrafts, setPullLabelDrafts] = useState<Record<number, string>>({});
  const [pullProtocolDrafts, setPullProtocolDrafts] = useState<Record<number, PullProtocol>>({});
  const [hlsNameDrafts, setHlsNameDrafts] = useState<Record<number, string>>({});
  const [passphraseEnabled, setPassphraseEnabled] = useState<Record<number, boolean>>({});
  const [ristBufferValues, setRistBufferValues] = useState<Record<number, string>>({});
  const [srtLatencyValues, setSrtLatencyValues] = useState<Record<number, string>>({});
  const [ristStreamIdEnabled, setRistStreamIdEnabled] = useState<Record<number, boolean>>({});
  const [ristSecretEnabled, setRistSecretEnabled] = useState<Record<number, boolean>>({});
  const [modeSavingMap, setModeSavingMap] = useState<Record<number, boolean>>({});
  const [disconnectingMap, setDisconnectingMap] = useState<Record<number, boolean>>({});
  const [disconnectingHlsMap, setDisconnectingHlsMap] = useState<Record<number, boolean>>({});
  const timersRef = useRef<Record<string, ReturnType<typeof setTimeout>>>({});
  const initializedDraftIdsRef = useRef(new Set<number>());
  const pullStatusMap = runtimePullStatuses;
  const hlsStatusMap = runtimeHlsStatuses;

  useEffect(() => {
    const labels: Record<number, string> = {};
    const protocols: Record<number, PullProtocol> = {};
    const hlsNames: Record<number, string> = {};
    const passphrases: Record<number, boolean> = {};
    const ristBuffers: Record<number, string> = {};
    const srtLatencies: Record<number, string> = {};
    const ristStreamIds: Record<number, boolean> = {};
    const ristSecrets: Record<number, boolean> = {};

    const activeIds = new Set(transcoders.map((transcoder) => transcoder.id));
    initializedDraftIdsRef.current.forEach((id) => {
      if (!activeIds.has(id)) initializedDraftIdsRef.current.delete(id);
    });

    transcoders.forEach((t) => {
      if (initializedDraftIdsRef.current.has(t.id)) return;
      initializedDraftIdsRef.current.add(t.id);
      if (isPullEligible(t)) {
        labels[t.id] = t.pullLabel || t.platformDistributor || 'Pull';
        protocols[t.id] = normalizePullProtocol(t.pullProtocol || (t.protocol === 'rist' ? 'RIST' : 'SRT'));
        passphrases[t.id] = !!t.pullSecret;
        ristBuffers[t.id] = String(t.pullBufferMs || 250);
        srtLatencies[t.id] = String(t.pullLatencyMs || 250000);
        ristStreamIds[t.id] = !!t.pullKey;
        ristSecrets[t.id] = !!t.pullSecret;
      }
      if (t.protocol === 'hls') hlsNames[t.id] = t.platformDistributor || (t.isHlsSpecial === false ? 'HLS+' : 'HLS');
    });

    const mergeMissing = <T,>(setter: React.Dispatch<React.SetStateAction<Record<number, T>>>, values: Record<number, T>) => {
      setter((prev) => ({ ...values, ...prev }));
    };
    mergeMissing(setPullLabelDrafts, labels);
    mergeMissing(setPullProtocolDrafts, protocols);
    mergeMissing(setHlsNameDrafts, hlsNames);
    mergeMissing(setPassphraseEnabled, passphrases);
    mergeMissing(setRistBufferValues, ristBuffers);
    mergeMissing(setSrtLatencyValues, srtLatencies);
    mergeMissing(setRistStreamIdEnabled, ristStreamIds);
    mergeMissing(setRistSecretEnabled, ristSecrets);
  }, [transcoders]);

  useEffect(() => () => Object.values(timersRef.current).forEach(clearTimeout), []);

  const isBroadcaster = user?.role === 'BROADCASTER';
  const limits = useMemo(() => {
    const rawTotal = isBroadcaster ? Number(activeChannel?.outputLimit ?? 0) : Number(user?.outputLimit ?? 0);
    const rawSocial = isBroadcaster ? Number((activeChannel as any)?.socialStreamLimit || 0) : Number((user as any)?.socialStreamLimit || 0);
    const rawManagedService = isBroadcaster ? Number((activeChannel as any)?.managedServiceLimit || 0) : Number((user as any)?.managedServiceLimit || 0);
    const rawManagedServiceUpgrade = isBroadcaster ? Number((activeChannel as any)?.managedServiceUpgradeLimit || 0) : Number((user as any)?.managedServiceUpgradeLimit || 0);
    const rawCpu = isBroadcaster ? (activeChannel?.cpuLimit || 0) : (user?.cpuLimit || 0);
    const rawHls = isBroadcaster ? (activeChannel?.hlsLimit || 0) : (user?.hlsLimit || 0);
    const rawHlsPlus = isBroadcaster ? (activeChannel?.hlsPlusLimit || 0) : (user?.hlsPlusLimit || 0);
    return {
      limitManaged: rawCpu,
      limitManagedService: Math.max(0, rawManagedService + rawManagedServiceUpgrade),
      limitSocial: Math.max(0, rawSocial),
      limitDirect: Math.max(0, rawTotal),
      limitHls: rawHls,
      limitHlsPlus: rawHlsPlus,
      usedDirect: transcoders.filter((t) => t.videoCodec === 'copy' && t.protocol !== 'hls' && !isSocialStreamOutput(t) && !isManagedServiceOutput(t)).length,
      usedSocial: transcoders.filter(isSocialStreamOutput).length,
      usedManagedService: transcoders.filter(isManagedServiceOutput).length,
      usedManaged: transcoders.filter((t) => t.videoCodec !== 'copy' && t.protocol !== 'hls' && !isManagedServiceOutput(t)).length,
      usedHls: transcoders.filter((t) => t.protocol === 'hls' && t.isHlsSpecial === true).length,
      usedHlsPlus: transcoders.filter((t) => t.protocol === 'hls' && t.isHlsSpecial === false).length,
    };
  }, [activeChannel, isBroadcaster, transcoders, user]);

  const profileComplete = !!(user?.firstName && user?.lastName && user?.country);

  const handleSave = async (data: any) => {
    try {
      const payload = { ...data, channelId: activeChannel?.id };
      const clean = {
        ...payload,
        platformDistributor: data.platformDistributor === 'custom'
          ? 'Custom'
          : PLATFORM_PRESETS.find((p) => p.id === data.platformDistributor)?.name || data.platformDistributor,
      };
      if (editData) await updateTranscoder(editData, clean, editData.transcoderHashId || editData.hashId);
      else await createTranscoder(clean);
      showSnackbar('Saved successfully.', 'success');
      setModalOpen(false);
      setHlsMode(null);
      setEditData(undefined);
      setAddType(null);
      await refreshData();
    } catch (e: any) {
      showSnackbar(e.response?.data?.message || 'Operation failed', 'error');
    }
  };

  const queueAutosave = useCallback((key: string, action: () => Promise<void>, message: string) => {
    if (timersRef.current[key]) clearTimeout(timersRef.current[key]);
    timersRef.current[key] = setTimeout(async () => {
      try { await action(); } catch (e: any) { showSnackbar(e.response?.data?.message || message, 'error'); }
      delete timersRef.current[key];
    }, 700);
  }, [showSnackbar]);

  const queuePullSettingsAutosave = useCallback((t: Transcoder, overrides?: Partial<UpdateTranscoderPayload>) => {
    queueAutosave(`pull-${t.id}`, async () => {
      const protocol = normalizePullProtocol(overrides?.pullProtocol || pullProtocolDrafts[t.id] || t.pullProtocol || (t.protocol === 'rist' ? 'RIST' : 'SRT'));
      const payload: UpdateTranscoderPayload = {
        deliveryMode: 'PULL',
        pullLabel: (overrides?.pullLabel ?? pullLabelDrafts[t.id] ?? t.pullLabel ?? t.platformDistributor ?? 'Pull').trim() || 'Pull',
        pullProtocol: protocol,
        ...overrides,
      };
      if (protocol === 'RIST') {
        payload.pullBufferMs = Number(ristBufferValues[t.id] || t.pullBufferMs || 250);
        delete payload.pullLatencyMs;
      } else {
        payload.pullLatencyMs = Number(srtLatencyValues[t.id] || t.pullLatencyMs || 250000);
        delete payload.pullBufferMs;
      }
      await updateTranscoder(t, payload, t.transcoderHashId || t.hashId);
      await refreshData();
    }, 'Failed to save pull settings');
  }, [pullLabelDrafts, pullProtocolDrafts, queueAutosave, refreshData, ristBufferValues, srtLatencyValues]);

  const handleToggleDeliveryMode = async (t: Transcoder, checked: boolean) => {
    const timerKey = `pull-${t.id}`;
    if (timersRef.current[timerKey]) {
      clearTimeout(timersRef.current[timerKey]);
      delete timersRef.current[timerKey];
    }
    setModeSavingMap((prev) => ({ ...prev, [t.id]: true }));
    try {
      const nextMode: DeliveryMode = checked ? 'PULL' : 'PUSH';
      const protocol = normalizePullProtocol(pullProtocolDrafts[t.id] || t.pullProtocol || (t.protocol === 'rist' ? 'RIST' : 'SRT'));
      const payload: UpdateTranscoderPayload = { deliveryMode: nextMode };
      if (nextMode === 'PULL') {
        payload.pullLabel = pullLabelDrafts[t.id]?.trim() || t.pullLabel || t.platformDistributor || 'Pull';
        payload.pullProtocol = protocol;
        if (protocol === 'RIST') payload.pullBufferMs = Number(ristBufferValues[t.id] || t.pullBufferMs || 250);
        else payload.pullLatencyMs = Number(srtLatencyValues[t.id] || t.pullLatencyMs || 250000);
      }
      await updateTranscoder(t, payload, t.transcoderHashId || t.hashId);
      showSnackbar(nextMode === 'PULL' ? 'Mode -> Pull.' : 'Mode -> Push.', 'success');
      await refreshData();
    } catch (e: any) {
      showSnackbar(e.response?.data?.message || 'Failed to change mode', 'error');
    } finally {
      setModeSavingMap((prev) => {
        const next = { ...prev };
        delete next[t.id];
        return next;
      });
    }
  };

  const handleToggleOutputState = async (t: Transcoder, checked: boolean) => {
    try {
      await updateTranscoder(t, { isActive: checked }, t.transcoderHashId || t.hashId);
      showSnackbar(checked ? 'Output enabled.' : 'Output disabled.', 'success');
      refreshData();
    } catch (e: any) { showSnackbar(e.response?.data?.message || 'Failed to toggle', 'error'); }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Delete destination?')) return;
    try {
      const transcoder = transcoders.find((item) => item.id === id);
      await deleteTranscoder(transcoder || id, transcoder?.transcoderHashId || transcoder?.hashId);
      showSnackbar('Deleted.', 'info');
      refreshData();
    }
    catch (e: any) { showSnackbar(e.response?.data?.message || 'Delete failed', 'error'); }
  };

  const handleRotatePullKey = async (t: Transcoder) => {
    if (!window.confirm('Rotate port, stream ID, and passphrase? Existing clients will stop working until updated.')) return;
    try {
      const result = await rotateTranscoderPullKey(t, t.transcoderHashId || t.hashId);
      if (result.pullProtocol) setPullProtocolDrafts((prev) => ({ ...prev, [t.id]: normalizePullProtocol(result.pullProtocol) }));
      if (result.pullLatencyMs) setSrtLatencyValues((prev) => ({ ...prev, [t.id]: String(result.pullLatencyMs) }));
      if (result.pullBufferMs) setRistBufferValues((prev) => ({ ...prev, [t.id]: String(result.pullBufferMs) }));
      setPassphraseEnabled((prev) => ({ ...prev, [t.id]: !!result.pullSecret }));
      setRistStreamIdEnabled((prev) => ({ ...prev, [t.id]: !!result.pullKey }));
      setRistSecretEnabled((prev) => ({ ...prev, [t.id]: !!result.pullSecret }));
      showSnackbar('Pull link rotated.', 'success');
      await refreshData();
    }
    catch (e: any) { showSnackbar(e.response?.data?.message || 'Rotation failed', 'error'); }
  };

  const handlePassphraseToggle = async (t: Transcoder, checked: boolean) => {
    setPassphraseEnabled((prev) => ({ ...prev, [t.id]: checked }));
    try {
      const result = checked
        ? await rotateTranscoderPullSecret(t, t.transcoderHashId || t.hashId)
        : await clearTranscoderPullSecret(t, t.transcoderHashId || t.hashId);
      setPassphraseEnabled((prev) => ({ ...prev, [t.id]: !!result.pullSecret }));
      showSnackbar(checked ? 'Passphrase enabled.' : 'Passphrase disabled.', 'success');
      await refreshData();
    } catch (e: any) {
      setPassphraseEnabled((prev) => ({ ...prev, [t.id]: !!t.pullSecret }));
      showSnackbar(e.response?.data?.message || 'Failed to change passphrase', 'error');
    }
  };

  const handleRistSecretToggle = async (t: Transcoder, checked: boolean) => {
    setRistSecretEnabled((prev) => ({ ...prev, [t.id]: checked }));
    try {
      const result = checked
        ? await rotateTranscoderPullSecret(t, t.transcoderHashId || t.hashId)
        : await clearTranscoderPullSecret(t, t.transcoderHashId || t.hashId);
      setRistSecretEnabled((prev) => ({ ...prev, [t.id]: !!result.pullSecret }));
      await refreshData();
    }
    catch (e: any) {
      setRistSecretEnabled((prev) => ({ ...prev, [t.id]: !!t.pullSecret }));
      showSnackbar(e.response?.data?.message || 'Failed to change RIST secret', 'error');
    }
  };

  const handleDisconnectPullViewer = async (t: Transcoder) => {
    setDisconnectingMap((prev) => ({ ...prev, [t.id]: true }));
    try {
      const result = await disconnectPullOutput(t, t.transcoderHashId || t.hashId);
      const rotated = result.rotated;
      if (rotated?.pullProtocol) setPullProtocolDrafts((prev) => ({ ...prev, [t.id]: normalizePullProtocol(rotated.pullProtocol) }));
      if (rotated?.pullLatencyMs) setSrtLatencyValues((prev) => ({ ...prev, [t.id]: String(rotated.pullLatencyMs) }));
      if (rotated?.pullBufferMs) setRistBufferValues((prev) => ({ ...prev, [t.id]: String(rotated.pullBufferMs) }));
      setPassphraseEnabled((prev) => ({ ...prev, [t.id]: !!rotated?.pullSecret }));
      setRistStreamIdEnabled((prev) => ({ ...prev, [t.id]: !!rotated?.pullKey }));
      setRistSecretEnabled((prev) => ({ ...prev, [t.id]: !!rotated?.pullSecret }));
      showSnackbar('Pull client disconnected.', 'success');
      await refreshData();
    }
    catch (e: any) { showSnackbar(e.response?.data?.message || 'Disconnect failed', 'error'); }
    finally { setDisconnectingMap((prev) => ({ ...prev, [t.id]: false })); }
  };

  const handleDisconnectHls = async (t: Transcoder) => {
    setDisconnectingHlsMap((prev) => ({ ...prev, [t.id]: true }));
    try {
      await disconnectHlsOutput(t, t.transcoderHashId || t.hashId);
      showSnackbar('HLS link disconnected and rotated.', 'success');
      refreshData();
    }
    catch (e: any) { showSnackbar(e.response?.data?.message || 'Failed to disconnect HLS link', 'error'); }
    finally { setDisconnectingHlsMap((prev) => ({ ...prev, [t.id]: false })); }
  };

  const openEdit = (t: Transcoder) => {
    if (t.protocol === 'hls') { setEditData(t); setHlsMode(t.isHlsSpecial === false ? 'HLS_SCTE35' : 'HLS_STANDARD'); return; }
    setEditData(t); setAddType(null); setModalOpen(true);
  };

  const addOutput = (type: 'DIRECT' | 'SOCIAL' | 'SERVICE' | 'MANAGED') => {
    const full = type === 'DIRECT'
      ? limits.usedDirect >= limits.limitDirect
      : type === 'SOCIAL'
        ? limits.usedSocial >= limits.limitSocial
        : type === 'SERVICE'
          ? limits.usedManagedService >= limits.limitManagedService
          : limits.usedManaged >= limits.limitManaged;
    if (full) { showSnackbar(`${type === 'DIRECT' ? 'Direct Pass' : type === 'SOCIAL' ? 'Social Stream' : type === 'SERVICE' ? 'Managed Service' : 'Remapped Output'} limit reached.`, 'warning'); return; }
    setEditData(undefined); setAddType(type); setModalOpen(true);
  };

  const addHls = (mode: HlsMode) => {
    if (mode === 'HLS_STANDARD' && limits.usedHls >= limits.limitHls) { showSnackbar('HLS limit reached. Purchase Add-on.', 'warning'); return; }
    if (mode === 'HLS_SCTE35' && limits.usedHlsPlus >= limits.limitHlsPlus) { showSnackbar('HLS+ limit reached. Purchase Add-on.', 'warning'); return; }
    setEditData(undefined); setHlsMode(mode);
  };

  const getStatusLabel = (t: Transcoder) => {
    const ps = pullStatusMap[t.id]?.status;
    if (!t.isActive) return 'Disabled';
    if (isPullOutput(t) && isPullEligible(t)) {
      return getPullHlsDisplayState(t, (outputHealthStatuses[t.id] || ps) as any).rowLabel;
    }
    if (t.protocol === 'hls') {
      return getPullHlsDisplayState(t, (outputHealthStatuses[t.id] || hlsStatusMap[t.id] || (t as any).hlsStatus || (t as any).hlsSession || null) as any).rowLabel;
    }
    if (outputHealthStatuses[t.id]?.status === 'connected') return 'Connected';
    if (outputHealthStatuses[t.id]?.status === 'connecting') return 'Connecting';
    if (t.status?.status === 'running' || t.status?.status === 'connected') return 'Connected';
    if (t.status?.status === 'connecting') return 'Connecting';
    return 'Disconnected';
  };

  const getOutputType = (t: Transcoder) => isManagedServiceOutput(t) ? 'Managed Service' : isSocialStreamOutput(t) ? 'Social Stream' : t.protocol === 'hls' ? (t.isHlsSpecial === false ? 'HLS+' : 'HLS') : isPullOutput(t) && isPullEligible(t) ? 'Pull Link' : t.videoCodec === 'copy' ? 'Direct Pass' : 'Remapped';
  const getMode = (t: Transcoder) => t.protocol === 'hls' ? 'Link' : isPullOutput(t) ? `Pull · ${normalizePullProtocol(t.pullProtocol || (t.protocol === 'rist' ? 'RIST' : 'SRT'))}` : 'Push';
  const getPrimaryUrl = (t: Transcoder) => t.protocol === 'hls' ? (hlsStatusMap[t.id]?.playbackUrl || t.playbackUrl || t.destination || '--') : isPullOutput(t) && isPullEligible(t) ? (normalizePullProtocol(t.pullProtocol || (t.protocol === 'rist' ? 'RIST' : 'SRT')) === 'RIST' ? getRistBaseUrl(t, pullStatusMap[t.id]?.status) : getSrtBaseUrl(t, pullStatusMap[t.id]?.status)) : (t.destination || '--');

  const filtered = useMemo(() => transcoders.filter((t) => {
    if (filter === 'ALL') return true;
    if (filter === 'DIRECT') return t.protocol !== 'hls' && t.videoCodec === 'copy' && !isPullOutput(t) && !isSocialStreamOutput(t) && !isManagedServiceOutput(t);
    if (filter === 'SOCIAL') return isSocialStreamOutput(t);
    if (filter === 'SERVICE') return isManagedServiceOutput(t);
    if (filter === 'MANAGED') return t.protocol !== 'hls' && t.videoCodec !== 'copy';
    if (filter === 'PULL') return isPullOutput(t) && isPullEligible(t);
    if (filter === 'HLS') return t.protocol === 'hls';
    return true;
  }), [filter, transcoders]);

  const counts: Record<FilterKey, number> = {
    ALL: transcoders.length,
    DIRECT: transcoders.filter((t) => t.protocol !== 'hls' && t.videoCodec === 'copy' && !isPullOutput(t) && !isSocialStreamOutput(t) && !isManagedServiceOutput(t)).length,
    SOCIAL: transcoders.filter(isSocialStreamOutput).length,
    SERVICE: transcoders.filter(isManagedServiceOutput).length,
    MANAGED: transcoders.filter((t) => t.protocol !== 'hls' && t.videoCodec !== 'copy' && !isManagedServiceOutput(t)).length,
    PULL: transcoders.filter((t) => isPullOutput(t) && isPullEligible(t)).length,
    HLS: transcoders.filter((t) => t.protocol === 'hls').length,
  };
  const connectedOutputs = transcoders.filter((t) => {
    if (!t.isActive) return false;
    const label = getStatusLabel(t).toLowerCase();
    return label === 'connected' || label === 'pulled';
  }).length;
  const activeOutputs = transcoders.filter((t) => t.isActive);
  const outputSummary = activeOutputs.length <= 0
    ? { value: '0 on', helper: 'No outputs on' }
    : connectedOutputs === activeOutputs.length
      ? { value: `${activeOutputs.length} on`, helper: `${connectedOutputs} connected` }
      : { value: `${activeOutputs.length} on`, helper: `${connectedOutputs} connected, ${activeOutputs.length - connectedOutputs} disconnected` };
  const activePullLinks = activeOutputs.filter((t) => isPullOutput(t) && isPullEligible(t)).length;
  const activeHlsOutputs = activeOutputs.filter((t) => t.protocol === 'hls').length;
  const totalCapacity = limits.limitDirect + limits.limitSocial + limits.limitManagedService + limits.limitManaged + limits.limitHls + limits.limitHlsPlus;
  const configuredOutputs = transcoders.length;

  if (!activeChannel) return <Box sx={{ px: 2, py: 4, textAlign: 'center' }}><Typography variant="body2" color="text.disabled">Select a channel to manage destinations.</Typography></Box>;

  return (
    <ConsolePageShell
      title="Destinations"
      eyebrow="Delivery Fabric"
      subtitle={`${activeChannel.name} delivery endpoints, pull links, and internal HLS outputs.${activeChannel.playkastrActive ? ' PlayKastr output is assigned from this page.' : ''}`}
      actions={(
        <>
          <Button size="small" variant="contained" startIcon={<ShoppingCartIcon />} onClick={() => setStoreOpen(true)} sx={actionBtnSx('var(--primary)', true)}>Store</Button>
          <Divider orientation="vertical" flexItem sx={{ mx: 0.25, borderColor: 'divider' }} />
          <Button size="small" variant="outlined" startIcon={<BoltIcon />} onClick={() => addOutput('DIRECT')} sx={actionBtnSx('var(--info)')}>Direct Pass</Button>
          <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={() => addOutput('SOCIAL')} sx={actionBtnSx('var(--success)')}>Social</Button>
          <Button size="small" variant="outlined" startIcon={<SupportAgentIcon />} onClick={() => addOutput('SERVICE')} sx={actionBtnSx('var(--warning)')}>Managed Service</Button>
          <Button size="small" variant="outlined" startIcon={<MemoryIcon />} onClick={() => addOutput('MANAGED')} sx={actionBtnSx('var(--accent)')}>Remapped</Button>
          <Button size="small" variant="outlined" startIcon={<HdIcon />} onClick={() => addHls('HLS_STANDARD')} sx={actionBtnSx('var(--secondary)')}>HLS</Button>
          <Button size="small" variant="outlined" startIcon={<AutoAwesomeIcon />} onClick={() => addHls('HLS_SCTE35')} sx={actionBtnSx('var(--primary)')}>HLS+</Button>
        </>
      )}
      stats={[
        { label: 'Outputs', value: outputSummary.value, helper: outputSummary.helper, tone: connectedOutputs > 0 ? 'success' : 'default' },
        { label: 'Pull Links', value: activePullLinks, helper: 'active pull destinations' },
        { label: 'Capacity', value: `${configuredOutputs}/${totalCapacity}`, helper: `${activeOutputs.length} on, ${Math.max(0, totalCapacity - configuredOutputs)} slots free`, tone: configuredOutputs >= totalCapacity && totalCapacity > 0 ? 'warning' : 'default' },
        { label: 'HLS Family', value: activeHlsOutputs, helper: `${activeOutputs.filter((t) => t.protocol === 'hls' && t.isHlsSpecial === false).length} active HLS+`, tone: activeHlsOutputs > 0 ? 'success' : 'default' },
      ]}
    >
      {(!profileComplete || transcoders.length === 0) && <Stack spacing={1.1} sx={{ mb: 2 }}>{!profileComplete && <Alert severity="warning" sx={{ borderRadius: 0 }}>Complete your profile in Settings before handing this console to operators.</Alert>}{transcoders.length === 0 && <Alert severity="info" sx={{ borderRadius: 0 }}>No destinations configured for this channel yet.</Alert>}</Stack>}

      <Box sx={{ border: '1px solid', borderColor: 'divider', bgcolor: 'background.paper', borderRadius: 0, mb: 2 }}>
        <Box sx={{ px: 1.5, py: 1.1, display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center' }}>
          <CapacityBar label="Direct" used={limits.usedDirect} limit={limits.limitDirect} /><CapacityBar label="Social" used={limits.usedSocial} limit={limits.limitSocial} /><CapacityBar label="Managed Service" used={limits.usedManagedService} limit={limits.limitManagedService} /><CapacityBar label="Remapped" used={limits.usedManaged} limit={limits.limitManaged} /><CapacityBar label="HLS" used={limits.usedHls} limit={limits.limitHls} /><CapacityBar label="HLS+" used={limits.usedHlsPlus} limit={limits.limitHlsPlus} />
        </Box>
      </Box>

      <Box sx={{ display: 'flex', flexWrap: { xs: 'nowrap', sm: 'wrap' }, gap: 0.75, mb: 1.5, overflowX: 'auto', pb: { xs: 0.5, sm: 0 }, pr: 0.25 }}>
        {(['ALL', 'DIRECT', 'SOCIAL', 'SERVICE', 'MANAGED', 'PULL', 'HLS'] as FilterKey[]).map((key) => (
          <Chip
            key={key}
            label={`${key === 'ALL' ? 'All' : key === 'SERVICE' ? 'Managed' : key.charAt(0) + key.slice(1).toLowerCase()} ${counts[key]}`}
            size="small"
            onClick={() => setFilter(key)}
            color={filter === key ? 'primary' : 'default'}
            variant={filter === key ? 'filled' : 'outlined'}
            sx={consoleFilterChipSx(theme, filter === key)}
          />
        ))}
      </Box>

      <Box sx={{ border: '1px solid', borderColor: 'divider', bgcolor: 'background.paper', borderRadius: 0, overflow: 'hidden' }}>
        <Box sx={{ display: { xs: 'none', md: 'grid' }, gridTemplateColumns: '34px 130px minmax(180px,1.4fr) 120px 120px minmax(200px,1.6fr) 92px 190px', alignItems: 'center', gap: 1, px: 1.25, py: 0.8, bgcolor: theme.palette.mode === 'dark' ? alpha(theme.palette.common.white, 0.055) : alpha(theme.palette.common.black, 0.045), borderBottom: '1px solid', borderColor: 'divider' }}><Box />{['Status', 'Name', 'Type', 'Mode', 'Destination / URL', 'Active', 'Actions'].map((h) => <Typography key={h} sx={{ ...labelSx, color: 'text.primary' }}>{h}</Typography>)}</Box>
        {filtered.length === 0 ? <Box sx={{ px: 2, py: 4, textAlign: 'center' }}><Typography variant="body2" color="text.disabled">No destinations match this filter.</Typography></Box> : filtered.map((t) => {
          const statusLabel = getStatusLabel(t);
          const normalizedStatusLabel = statusLabel.toLowerCase();
          const statusKey = normalizedStatusLabel === 'pulled' || normalizedStatusLabel === 'connected'
            ? 'connected'
            : normalizedStatusLabel === 'disabled'
              ? 'disabled'
              : normalizedStatusLabel === 'connecting'
                ? 'connecting'
              : 'disconnected';
          const statusColor = STATUS_COLOR[statusKey] || theme.palette.text.disabled;
          const pullStatus = pullStatusMap[t.id]?.status;
          const hlsEnvelope = hlsStatusMap[t.id] || null;
          const hlsSession = hlsEnvelope?.activeSession;
          const destinationHealth = outputHealthStatuses[t.id] || (pullStatusMap[t.id] as any)?.destinationHealth || (hlsStatusMap[t.id] as any)?.destinationHealth || null;
          const isExpanded = !!expanded[t.id];
          const isPull = isPullOutput(t) && isPullEligible(t);
          const isHls = t.protocol === 'hls';
          const canDirectPullMode = isPullEligible(t) && !isSocialStreamOutput(t) && !isHls;
          const showPullControls = canDirectPullMode && isPull;
          const showPullToggleOnly = canDirectPullMode && !isPull;
          const pullProtocol = normalizePullProtocol(pullProtocolDrafts[t.id] || t.pullProtocol || (t.protocol === 'rist' ? 'RIST' : 'SRT'));
          const runtimePullKey = pullStatusMap[t.id]?.pullKey || pullStatusMap[t.id]?.status?.pullKey || t.pullKey || null;
          const runtimePullSecret = pullStatusMap[t.id]?.pullSecret || pullStatusMap[t.id]?.status?.pullSecret || t.pullSecret || null;
          const primaryUrl = isPull ? (pullProtocol === 'RIST' ? getRistBaseUrl(t, pullStatus) : getSrtBaseUrl(t, pullStatus)) : getPrimaryUrl(t);
          const previewPullUrl = pullProtocol === 'RIST'
            ? buildRistPreviewPullUrl(primaryUrl, ristBufferValues[t.id] || '250', !!ristStreamIdEnabled[t.id], runtimePullKey, !!ristSecretEnabled[t.id], runtimePullSecret)
            : buildSrtPreviewPullUrl(primaryUrl, srtLatencyValues[t.id] || '250000', !!passphraseEnabled[t.id], runtimePullSecret);
          const destinationMetrics = getDestinationHealthMetrics(t, destinationHealth || (isPull ? pullStatus : isHls ? (hlsEnvelope || hlsSession) : null));
          const rowSignature = [
            isExpanded ? 1 : 0,
            statusLabel,
            primaryUrl,
            previewPullUrl,
            pullLabelDrafts[t.id] || '',
            pullProtocol,
            hlsNameDrafts[t.id] || '',
            passphraseEnabled[t.id] ? 1 : 0,
            ristBufferValues[t.id] || '',
            srtLatencyValues[t.id] || '',
            ristStreamIdEnabled[t.id] ? 1 : 0,
            ristSecretEnabled[t.id] ? 1 : 0,
            modeSavingMap[t.id] ? 1 : 0,
            disconnectingMap[t.id] ? 1 : 0,
            disconnectingHlsMap[t.id] ? 1 : 0,
            pullStatus?.startedAt || '',
            pullStatus?.currentKbps || 0,
            pullStatus?.totalBytesOut || 0,
            pullStatus?.currentViewer?.ip || '',
            hlsSession?.startedAt || '',
            hlsSession?.averageKbps || 0,
            hlsSession?.bytesServed || 0,
            hlsSession?.clientIp || '',
            destinationHealth?.status || '',
            destinationMetrics.ip || '',
            destinationMetrics.trafficKbps || 0,
          ].join('|');
          return (
            <DestinationRow key={t.id} transcoder={t} signature={rowSignature} themeMode={theme.palette.mode} render={() => (
            <Box sx={{ borderBottom: '1px solid', borderColor: 'divider', '&:last-child': { borderBottom: 0 } }}>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '28px minmax(0,1fr)', md: '34px 130px minmax(180px,1.4fr) 120px 120px minmax(200px,1.6fr) 92px 190px' }, gap: 1, alignItems: 'center', px: 1.25, py: 0.75, minHeight: 52, bgcolor: theme.palette.mode === 'dark' ? alpha(theme.palette.common.white, 0.018) : alpha(theme.palette.common.black, 0.012), color: 'text.primary', '&:hover': { bgcolor: theme.palette.mode === 'dark' ? alpha(theme.palette.common.white, 0.075) : alpha(theme.palette.common.black, 0.055) } }}>
                <IconButton size="small" onClick={() => setExpanded((prev) => ({ ...prev, [t.id]: !prev[t.id] }))} sx={{ borderRadius: 0 }}>{isExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}</IconButton>
                <Stack direction="row" spacing={0.9} alignItems="center" sx={{ display: { xs: 'none', md: 'flex' } }}><Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: statusColor, animation: statusKey === 'connected' ? `${pulseDot} 1.4s ease-in-out infinite` : 'none' }} /><Typography sx={{ fontSize: '0.76rem', fontWeight: 700, color: statusColor }} noWrap>{statusLabel}</Typography></Stack>
                <Box sx={{ minWidth: 0, gridColumn: { xs: '2 / 3', md: 'auto' } }}>
                  <Typography sx={{ fontSize: '0.82rem', fontWeight: 700 }} noWrap>{isPull ? (pullLabelDrafts[t.id] || t.pullLabel || t.platformDistributor || 'Pull Link') : isHls ? (hlsNameDrafts[t.id] || t.platformDistributor || 'HLS') : (t.platformDistributor || 'Custom')}</Typography>
                  <Typography sx={{ fontSize: '0.7rem', color: theme.palette.mode === 'dark' ? alpha(theme.palette.common.white, 0.78) : alpha(theme.palette.common.black, 0.7) }} noWrap>{t.regionLocation || t.transcoderDisplayId || t.displayId || (t.transcoderHashId ? `TR-${t.transcoderHashId}` : 'Output')}</Typography>
                  <Stack direction="row" spacing={0.7} alignItems="center" sx={{ display: { xs: 'flex', md: 'none' }, mt: 0.4, minWidth: 0 }}>
                    <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: statusColor, animation: statusKey === 'connected' ? `${pulseDot} 1.4s ease-in-out infinite` : 'none' }} />
                    <Typography sx={{ fontSize: '0.68rem', fontWeight: 800, color: statusColor }} noWrap>{statusLabel}</Typography>
                    <Typography sx={{ ...monoSx, fontSize: '0.66rem', color: 'text.secondary' }} noWrap>{getOutputType(t)}</Typography>
                  </Stack>
                </Box>
                <Stack direction="row" spacing={0.5} alignItems="center" sx={{ display: { xs: 'none', md: 'flex' }, minWidth: 0 }}><Typography sx={{ ...monoSx }} noWrap>{getOutputType(t)}</Typography>{isSocialStreamOutput(t) && <Chip label="Social" size="small" color="primary" sx={{ borderRadius: 0, height: 20, fontSize: '0.62rem', fontWeight: 800 }} />}</Stack>
                <Typography sx={{ ...monoSx, display: { xs: 'none', md: 'block' } }} noWrap>{getMode(t)}</Typography>
                <Tooltip title={primaryUrl} arrow><Typography sx={{ ...monoSx, color: theme.palette.mode === 'dark' ? alpha(theme.palette.common.white, 0.82) : alpha(theme.palette.common.black, 0.72), display: { xs: 'none', md: 'block' } }} noWrap>{primaryUrl || '--'}</Typography></Tooltip>
                <Switch size="small" checked={!!t.isActive} onChange={(e) => handleToggleOutputState(t, e.target.checked)} sx={{ display: { xs: 'none', md: 'inline-flex' } }} />
                <Stack
                  direction="row"
                  spacing={0.35}
                  justifyContent={{ xs: 'flex-start', md: 'flex-end' }}
                  flexWrap="wrap"
                  useFlexGap
                  sx={{ gridColumn: { xs: '2 / 3', md: 'auto' }, mt: { xs: 0.35, md: 0 } }}
                >
                  <Chip
                    label={t.isActive ? 'Active' : 'Disabled'}
                    size="small"
                    color={t.isActive ? 'success' : 'default'}
                    onClick={() => handleToggleOutputState(t, !t.isActive)}
                    sx={{ display: { xs: 'inline-flex', md: 'none' }, borderRadius: 0, fontSize: '0.68rem', fontWeight: 800, cursor: 'pointer' }}
                  />
                  <Tooltip title="Copy URL"><IconButton size="small" onClick={() => copyText(primaryUrl, () => showSnackbar('Copied.', 'success'))} sx={consoleIconButtonSx(theme)}><ContentCopyIcon fontSize="small" /></IconButton></Tooltip>
                  {isPull && <Tooltip title="Rotate pull key"><IconButton size="small" onClick={() => handleRotatePullKey(t)} sx={{ ...consoleIconButtonSx(theme), color: 'secondary.main' }}><AutorenewIcon fontSize="small" /></IconButton></Tooltip>}
                  {isPull && <Tooltip title="Disconnect pull client"><span><IconButton size="small" disabled={disconnectingMap[t.id]} onClick={() => handleDisconnectPullViewer(t)} sx={{ ...consoleIconButtonSx(theme), color: 'error.main' }}><LinkOffIcon fontSize="small" /></IconButton></span></Tooltip>}
                  {isHls && <Tooltip title="Rotate HLS link"><span><IconButton size="small" disabled={disconnectingHlsMap[t.id]} onClick={() => handleDisconnectHls(t)} sx={{ ...consoleIconButtonSx(theme), color: 'secondary.main' }}><LinkOffIcon fontSize="small" /></IconButton></span></Tooltip>}
                  <Tooltip title="Edit"><span><IconButton size="small" onClick={() => openEdit(t)} sx={consoleIconButtonSx(theme)}><EditIcon fontSize="small" /></IconButton></span></Tooltip>
                  <Tooltip title="Delete"><IconButton size="small" onClick={() => handleDelete(t.id)} sx={{ ...consoleIconButtonSx(theme), color: 'error.main' }}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                </Stack>
              </Box>
              <Collapse in={isExpanded} unmountOnExit><Box sx={{ px: { xs: 1, sm: 1.5 }, py: 1.25, bgcolor: theme.palette.mode === 'dark' ? alpha(theme.palette.common.white, 0.04) : alpha(theme.palette.common.black, 0.028), borderTop: '1px solid', borderColor: 'divider' }}><Stack spacing={1.25}>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(4, 1fr)' }, gap: 1.5 }}><StatCell label="Video" value={`${t.videoCodec || '--'} / ${t.resolution || 'copy'} / ${t.videoBitrate || 'copy'}`} /><StatCell label="Audio" value={`${t.audioCodec || '--'} / ${t.audioBitrate || 'copy'}`} /><StatCell label="Started" value={formatDateTime(t.status?.startedAt || pullStatus?.startedAt || hlsSession?.startedAt)} /><StatCell label="Traffic" value={isPull ? `${formatKbps(pullStatus?.currentKbps)} · ${formatBytes(pullStatus?.totalBytesOut)}` : isHls ? `${formatKbps(hlsSession?.averageKbps)} · ${formatBytes(hlsSession?.bytesServed)}` : (t.status?.stats?.bitrate || '--')} /></Box>
                {showPullToggleOnly && <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '220px 1fr' }, gap: 1.25, alignItems: 'start' }}><Stack spacing={0.5}><FormControlLabel control={<Switch size="small" checked={false} disabled={!!modeSavingMap[t.id]} onChange={(e) => handleToggleDeliveryMode(t, e.target.checked)} />} label="Pull mode" /><Typography variant="caption" color="text.secondary">Push is active. Enable pull mode to expose the SRT/RIST listener and its controls.</Typography></Stack><Alert severity="info" sx={{ borderRadius: 0, py: 0.75 }}>Pull-only options stay hidden while this destination is in push mode.</Alert></Box>}
                {showPullControls && <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1.2fr 1.3fr 1fr' }, gap: 1.25, alignItems: 'start' }}><Stack spacing={0.8}><Typography sx={consoleSectionHeadingSx}>Pull Identity</Typography><TextField label="Pull Label" size="small" value={pullLabelDrafts[t.id] || ''} onChange={(e) => { setPullLabelDrafts((prev) => ({ ...prev, [t.id]: e.target.value })); queuePullSettingsAutosave(t, { pullLabel: e.target.value }); }} sx={fieldSx} /><FormControl size="small" sx={fieldSx}><Select value={pullProtocol} onChange={(e) => { const next = normalizePullProtocol(e.target.value as PullProtocol); setPullProtocolDrafts((prev) => ({ ...prev, [t.id]: next })); queuePullSettingsAutosave(t, { pullProtocol: next }); }}><MenuItem value="SRT">SRT Pull</MenuItem><MenuItem value="RIST">RIST Pull</MenuItem></Select></FormControl></Stack><Stack spacing={0.8}><Typography sx={consoleSectionHeadingSx}>Pull Access</Typography><TextField label={pullProtocol === 'RIST' ? 'RIST URL' : 'SRT URL'} size="small" value={previewPullUrl} InputProps={{ readOnly: true, endAdornment: <InputAdornment position="end"><IconButton size="small" onClick={() => copyText(previewPullUrl, () => showSnackbar('Copied.', 'success'))} sx={consoleIconButtonSx(theme)}><ContentCopyIcon fontSize="small" /></IconButton></InputAdornment> }} sx={fieldSx} /><Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap><Button size="small" variant="outlined" startIcon={<AutorenewIcon />} onClick={() => handleRotatePullKey(t)} sx={smallBtnSx}>Rotate</Button><Button size="small" variant="outlined" color="error" startIcon={<LinkOffIcon />} disabled={disconnectingMap[t.id]} onClick={() => handleDisconnectPullViewer(t)} sx={smallBtnSx}>Disconnect</Button></Stack></Stack><Stack spacing={0.8}><Typography sx={consoleSectionHeadingSx}>Runtime Controls</Typography><FormControlLabel control={<Switch size="small" checked={isPullOutput(t)} disabled={!!modeSavingMap[t.id]} onChange={(e) => handleToggleDeliveryMode(t, e.target.checked)} />} label="Pull mode" />{pullProtocol === 'SRT' ? <FormControlLabel control={<Checkbox size="small" checked={!!passphraseEnabled[t.id]} onChange={(e) => handlePassphraseToggle(t, e.target.checked)} />} label="SRT passphrase" /> : <><FormControlLabel control={<Checkbox size="small" checked={!!ristStreamIdEnabled[t.id]} onChange={(e) => { const checked = e.target.checked; setRistStreamIdEnabled((prev) => ({ ...prev, [t.id]: checked })); queuePullSettingsAutosave(t, { pullProtocol: 'RIST' }); }} />} label="RIST stream ID" /><FormControlLabel control={<Checkbox size="small" checked={!!ristSecretEnabled[t.id]} onChange={(e) => handleRistSecretToggle(t, e.target.checked)} />} label="RIST secret" /></>}<Typography variant="caption" color="text.secondary">Viewer: {pullStatus?.currentViewer?.ip || '--'} · {buildGeoLabel(pullStatus)}</Typography></Stack></Box>}
                {isHls && <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1.4fr 1fr' }, gap: 1.25 }}><TextField label="HLS Name" size="small" value={hlsNameDrafts[t.id] || ''} onChange={(e) => { setHlsNameDrafts((prev) => ({ ...prev, [t.id]: e.target.value })); queueAutosave(`hls-${t.id}`, async () => { await updateTranscoder(t, { platformDistributor: e.target.value.trim() || (t.isHlsSpecial === false ? 'HLS+' : 'HLS') }, t.transcoderHashId || t.hashId); await refreshData(); }, 'Failed to save HLS name'); }} sx={fieldSx} /><TextField label="Playback URL" size="small" value={primaryUrl} InputProps={{ readOnly: true, endAdornment: <InputAdornment position="end"><IconButton size="small" onClick={() => copyText(primaryUrl, () => showSnackbar('Copied.', 'success'))}><ContentCopyIcon fontSize="small" /></IconButton></InputAdornment> }} sx={fieldSx} /><Stack direction="row" spacing={1} alignItems="center"><Button size="small" variant="outlined" color="error" startIcon={<LinkOffIcon />} disabled={disconnectingHlsMap[t.id]} onClick={() => handleDisconnectHls(t)} sx={smallBtnSx}>Rotate link</Button><Typography variant="caption" color="text.secondary">Viewer: {hlsSession?.clientIp || '--'} · {buildHlsGeoLabel(hlsSession)}</Typography></Stack></Box>}
              </Stack></Box></Collapse>
            </Box>
            )} />
          );
        })}
      </Box>

      <DestinationModal open={isModalOpen} onClose={() => { setModalOpen(false); setEditData(undefined); setAddType(null); }} onSave={handleSave} initialData={editData} userRole={user?.role || ''} mode={addType} />
      {hlsMode && <HLSModal open={!!hlsMode} onClose={() => { setHlsMode(null); setEditData(undefined); }} onSave={handleSave} mode={hlsMode} initialData={editData} />}
      <ConsoleDialogFrame
        open={isStoreOpen}
        onClose={() => setStoreOpen(false)}
        maxWidth="lg"
        fullWidth
        icon={<StorefrontIcon />}
        title="Add-on Store"
        subtitle="Allocate more delivery capacity, upgrades, and channel services without leaving the destinations console."
      >
        <Box sx={{ mx: -2, my: -2 }}>
          <AddonStore />
        </Box>
      </ConsoleDialogFrame>
    </ConsolePageShell>
  );
};
